import { CalendarBlankIcon, MapPinIcon, UserIcon } from "@phosphor-icons/react";
import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { Loader } from "apps/visits/packages/ui/src/components";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import type { ItemListId } from "../../tracking/create-ecommerce-event";
import {
  Error,
  FlowSteps,
  GetOrderError,
  PayUInfo,
  SelectTimeSlotModal,
  SideCart,
  SummaryBox,
  SummaryPersonalDetails,
  Timer,
  TimeSlotInfo,
} from "../components";
import { useConfirmOrderHandler } from "../hooks/use-confirm-order-handler";
import { useOrderData } from "../hooks/use-order-data";
import { useCartStore } from "../store/use-cart-store";
import { useGetConfig } from "../store/use-config";
import { safeTrackEvent, viewPaymentInfoEvent } from "../tracking/events";
import { formatAddress } from "../utils/format-address";
import { readGaUserType } from "../utils/ga-user-type";

export const Summary = () => {
  const { t } = useTranslation("translation", { keyPrefix: "GROUP_ACTIONS_ORDERING.SUMMARY" });
  const config = useGetConfig();
  const { uuid } = useParams<{ uuid: string }>();
  const {
    data,
    isLoading: isOrderLoading,
    error,
    isSelectTimeSlotModalOpen,
    setIsSelectTimeSlotModalOpen,
  } = useOrderData(uuid);

  const { handleConfirm, confirmOrderError, isLoading } = useConfirmOrderHandler(uuid);

  // REV-114: fire view_payment_info once per loaded order (re-mounts re-fire intentionally;
  // single uuid guard).
  // REV-15: fire for free (0 zł) orders too — the user entered the payment-info step regardless of
  // basket value, and funnel reporting needs this view emitted symmetrically with begin_checkout /
  // purchase (both unguarded). Mirrors the voucher-distribution convention (REV-164).
  const trackedViewForUuidRef = useRef<string>();
  useEffect(() => {
    if (!data?.order) return;
    if (trackedViewForUuidRef.current === data.order.uuid) return;
    trackedViewForUuidRef.current = data.order.uuid;

    // Cart still holds the products at summary time — pull item_list_id by uuid for attribution.
    const cartProducts = useCartStore.getState().products;
    const itemListIdsByUuid = cartProducts.reduce<Record<string, ItemListId>>((acc, p) => {
      if (p.item_list_id) acc[p.uuid] = p.item_list_id;
      return acc;
    }, {});

    const items = data.order.items.map((item) => ({
      uuid: item.productUuid,
      name: item.name,
      grossPrice: String(item.grossPrice),
      productType: "blood-test-package" as const,
      quantity: 1,
      item_list_id: itemListIdsByUuid[item.productUuid] ?? ("listing" as ItemListId),
    }));

    safeTrackEvent(
      () =>
        viewPaymentInfoEvent(
          items,
          { value: Number(data.order.totalGrossPrice) },
          { numberOfProducts: items.length, userType: readGaUserType() },
        ),
      "view_payment_info",
      "summary mount",
    );
  }, [data?.order]);

  if (isOrderLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <GetOrderError />;
  }

  const isPayment = data?.order.items.some((item) => item.grossPrice > 0) || false;
  const dayjsTimeSlot = dayjs(data?.order.visitTime);

  return (
    <>
      <IntersectionFadeIn className="mx-auto w-full max-w-[1275px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12">
        <FlowSteps />
        <div className="mb-6 flex flex-col items-start">
          <h1 className="font-heading font-serif text-2xl lg:text-4xl">{isPayment ? t("PAY") : t("CONFIRM")}</h1>
          <p className="mt-2 text-base text-muted-foreground lg:text-lg">
            {isPayment ? t("HEADER_WITH_PAYMENT") : t("HEADER_WITHOUT_PAYMENT")}
          </p>
        </div>
        <SidebarLayout>
          <div className="flex flex-col gap-6">
            <Timer isPayment={isPayment} />
            <SummaryBox icon={<UserIcon />} header={t("CUSTOMER_DETAILS")} tooltip={t("CUSTOMER_DETAILS_TOOLTIP")}>
              <SummaryPersonalDetails />
            </SummaryBox>
            <SummaryBox icon={<MapPinIcon />} header={t("ADDRESS")}>
              <p>{formatAddress(config.address)}</p>
            </SummaryBox>
            {config.withCalendar && (
              <SummaryBox icon={<CalendarBlankIcon />} header={t("TIME")}>
                <TimeSlotInfo timeSlot={dayjsTimeSlot} />
              </SummaryBox>
            )}
          </div>
          <SideCart
            readOnly
            onNextStep={handleConfirm}
            buttonLabel={isPayment ? t("PAY") : t("CONFIRM")}
            ctaLoading={isLoading}
            appendedContent={<Error error={confirmOrderError} className="mt-2" />}
            belowCart={isPayment && <PayUInfo />}
          />
        </SidebarLayout>
      </IntersectionFadeIn>
      {config.withCalendar && (
        <SelectTimeSlotModal open={isSelectTimeSlotModalOpen} onClose={() => setIsSelectTimeSlotModalOpen(false)} />
      )}
    </>
  );
};
